import PassengerBookingAttributes from '@/components/passenger/PassengerBookingAttributes';
import PassengerInfo from '@/components/purchase/checkout/PassengerInfo';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import type { PersonDataField } from '@/hooks/usePersonFieldRenderer';
import { useSelector } from '@/store/utils';
import { buildPassengerTypeByPassengerIdFromBooking } from '@/utils/booking';
import { getPassengerName } from '@/utils/passenger';
import type { CheckoutValues } from '@/utils/zodSchema';
import { Divider, Typography } from '@packages/webshop-design-system';
import { Fragment, useMemo, type FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

interface PassengerListProps {
  formFields: Array<PersonDataField>;
}

const PassengerList: FC<PassengerListProps> = ({ formFields }) => {
  const { control, watch } = useFormContext<CheckoutValues>();
  const booking = useSelector(purchaseFlowBookingSelector);
  const { fields } = useFieldArray({
    control,
    name: 'passengers',
  });
  const passengers = watch('passengers');
  const passengerTypeByPassengerId = useMemo(
    () => buildPassengerTypeByPassengerIdFromBooking(booking),
    [booking]
  );

  return (
    <div className="flex flex-col gap-3">
      {fields.map(({ id }, index) => {
        const passenger = passengers[index];
        const bookingPassenger = booking?.passengers?.find(
          (bookingPassenger) => bookingPassenger.id === passenger.id
        );

        return (
          <Fragment key={id}>
            <div
              className="flex flex-col gap-3 tablet:flex-row tablet:gap-6"
              data-testid={`passengers-${index}-content`}
            >
              <div className="tablet:flex-1">
                <Typography
                  variant="body1-bold"
                  className="whitespace-normal break-all text-dark"
                  as="h3"
                >
                  {getPassengerName(passenger, index)}
                </Typography>
                <PassengerBookingAttributes
                  passenger={bookingPassenger}
                  passengerType={
                    passenger.id
                      ? passengerTypeByPassengerId[passenger.id]
                      : undefined
                  }
                />
              </div>
              <PassengerInfo
                formFields={formFields}
                prefix={`passengers[${index}]`}
              />
            </div>
            {index < fields.length - 1 && <Divider variant="medium" />}
          </Fragment>
        );
      })}
    </div>
  );
};

export default PassengerList;
